import {
  Assignment,
  Cancel,
  CheckCircle,
  People,
  PlayArrow,
  Reply,
  Schedule,
  Send,
  TrendingUp,
  Email,
  CheckCircleOutline,
  Twitter,
  PersonAdd,
  Link,
  Message,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { styled, alpha } from '@mui/material/styles';

// Utility function to shorten numbers
const fShortenNumber = (num: number): string => {
  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return num.toString();
};

// Get icon based on stat name
const getStatConfig = (name: string) => {
  const configs: { [key: string]: { icon: any } } = {
    'Total Contacts': { icon: People },
    Connections: { icon: Send },
    'Total Contacted': { icon: CheckCircle },
    Uncontacted: { icon: Cancel },
    '1st Follow Up Remaining': { icon: Schedule },
    '2nd Follow Up Remaining': { icon: Schedule },
    '3rd Follow Up Remaining': { icon: Schedule },
    '1st Follow Up Completed': { icon: CheckCircle },
    '2nd Follow Up Completed': { icon: CheckCircle },
    '3rd Follow Up Completed': { icon: CheckCircle },
    'Total Follow Ups': { icon: Assignment },
    'Active Follow Ups': { icon: PlayArrow },
    'Total Handles': { icon: People },
    'Active Handles': { icon: CheckCircle },
    'Tweets Processed': { icon: TrendingUp },
    'Total Replies': { icon: Reply },
    // Analytics metrics
    'Connections Sent': { icon: Send },
    'Emails Sent': { icon: Email },
    'Connections Accepted': { icon: CheckCircleOutline },
    'Twitter DMs Sent': { icon: Twitter },
    'Telegram DMs Sent': { icon: Message },
    'Leads Generated': { icon: PersonAdd },
    'Existing Connections': { icon: Link },
  };

  return configs[name] || { icon: TrendingUp };
};

// Styled component
const RootStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color?: string }>(({ color }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 20px',
  background: 'rgba(255, 255, 255, 0.01)',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  borderRadius: '20px',
  minHeight: 110,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'default',
  overflow: 'hidden',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.03)',
    borderColor: alpha(color || '#fff', 0.15),
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha('#000', 0.3)}`,
    '& .metric-icon-bg': {
      transform: 'scale(1.1) rotate(5deg)',
      background: alpha(color || '#fff', 0.1),
    },
  },
}));

// Define props type
interface AppVerifiedUsersProps {
  name: string;
  count: number;
  color?: string; // Add color for left border
}

export default function AppVerifiedUsers({ name, count, color }: AppVerifiedUsersProps) {
  const config = getStatConfig(name);
  const IconComponent = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <RootStyle color={color}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            gap: 1.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            className='metric-icon-bg'
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              bgcolor: alpha(color || '#fff', 0.05),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              transition: 'all 0.4s ease',
            }}
          >
            <IconComponent sx={{ fontSize: '1.1rem' }} />
          </Box>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.35)',
              fontWeight: 800,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              fontFamily: 'Lato, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
        </Box>

        <Typography
          className='metric-value'
          sx={{
            fontWeight: 900,
            fontSize: '1.9rem',
            color: '#fff',
            letterSpacing: '-0.5px',
            lineHeight: 1,
            fontFamily: 'Lato, sans-serif',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {fShortenNumber(count)}
        </Typography>
      </RootStyle>
    </motion.div>
  );
}
